import React, { useState, useEffect, useRef } from 'react';
import { ShieldCheck, Sparkles, Droplet, ArrowRight, Send, CheckCircle, AlertCircle, Loader2, Phone } from 'lucide-react';
import { contactInfo } from '../data';

export default function Hero({ services = [], prefilledMessage = '', selectedService = '', onCtaClick, onSecondaryClick }) {
  const formRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    service: '',
    message: '',
    website_url: '' // Honeypot field
  });

  const [status, setStatus] = useState({
    submitting: false,
    success: false,
    error: null
  });

  // Handle prefilled message from price calculator or service selection
  useEffect(() => {
    if (prefilledMessage) {
      setFormData(prev => ({
        ...prev,
        message: prefilledMessage
      }));
    }
  }, [prefilledMessage]);

  useEffect(() => {
    if (selectedService) {
      setFormData(prev => ({
        ...prev,
        service: selectedService
      }));
    }
  }, [selectedService]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ submitting: true, success: false, error: null });

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const result = await response.json();

      if (response.ok && result.status === 'ok') {
        setStatus({
          submitting: false,
          success: true,
          error: null
        });
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          address: '',
          service: '',
          message: '',
          website_url: ''
        });
      } else {
        throw new Error(result.message || 'Der skete en fejl under afsendelsen. Prøv venligst igen.');
      }
    } catch (err) {
      setStatus({
        submitting: false,
        success: false,
        error: err.message || 'Der kunne ikke oprettes forbindelse til serveren. Tjek din internetforbindelse.'
      });
    }
  };

  return (
    <section id="home" className="relative bg-white overflow-hidden pt-28 sm:pt-32 lg:pt-40 pb-16 lg:pb-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          {/* Target Element: Container in Hero.jsx - now col-12 and following container lines */}
          <div className="lg:col-span-12 flex flex-col lg:flex-row gap-8 lg:gap-12 items-start justify-between">
            
            {/* Left Column: Image & Text */}
            <div className="w-full lg:w-1/2 space-y-6 p-0 m-0">
              {/* Image of Morten Ravn */}
              <img
                src="https://assets.auxodev.dk/assets/482dc8b9-e103-46bf-9437-c08a9e4baa31/dde1b13e-32c4-4abf-b9b3-d92c49029ac2.jpg"
                alt="Morten Ravn - Ravn's Vinduespolering"
                className="w-full h-[320px] sm:h-[450px] lg:h-[520px] rounded-2xl shadow-lg object-cover object-top brightness-[0.95] p-0 m-0 block"
              />

              {/* Text above webform */}
              <div className="space-y-3 text-left">
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight text-[#0f126e] uppercase pt-4 sm:pt-6">
                  Rene ruder uden striber. <br />
                  <span className="text-[#0f126e]">30 års erfaring.</span>
                </h1>

                <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                  Jeg leverer skinnende ruder til både private og erhverv i Hirtshals og omegn.<br />
                  Altid faste aftaler og fuld tilfredshedsgaranti.
                </p>

                <div className="pt-1">
                  <a
                    href="tel:21298387"
                    className="inline-flex items-center justify-center bg-[#0e227b] hover:bg-[#0e227b]/90 text-white font-semibold px-5 py-2.5 rounded-full shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm cursor-pointer space-x-2"
                  >
                    <Phone className="w-4 h-4" />
                    <span>Tlf 21298387</span>
                  </a>
                </div>
              </div>
            </div>

            {/* Right Column: Webform Card */}
            <div className="w-full lg:w-1/2 flex justify-center lg:justify-end">
              <div id="hero-form" className="bg-slate-50 text-slate-900 rounded-3xl p-6 sm:p-8 lg:p-10 shadow-xl border-t-4 border-red-800 relative w-full max-w-xl min-h-[550px] flex flex-col justify-between">
                
                {status.success ? (
                  <div className="text-center py-12 space-y-6 my-auto">
                    <div className="w-16 h-16 bg-[#0e227b]/10 text-emerald-600 rounded-full flex items-center justify-center mx-auto">
                      <CheckCircle className="w-10 h-10" />
                    </div>
                    <h3 className="text-2xl font-bold text-slate-900">Tak for din besked!</h3>
                    <p className="text-slate-600 text-sm">
                      Jeg har modtaget din forespørgsel og vender tilbage til dig med et uforpligtende tilbud hurtigst muligt.
                    </p>
                    <button
                      onClick={() => setStatus({ submitting: false, success: false, error: null })}
                      className="mt-6 bg-[#0e227b] hover:bg-[#0e227b]/90 text-white font-semibold px-6 py-3 rounded-full transition-colors w-full cursor-pointer text-sm shadow-md"
                    >
                      Send en ny besked
                    </button>
                  </div>
                ) : (
                  <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <h3 className="text-xl sm:text-2xl font-bold text-slate-900">Få et gratis tilbud</h3>
                      <p className="text-xs sm:text-sm text-slate-600">Udfyld formularen, så vender jeg tilbage inden for 24 timer.</p>
                    </div>

                    {/* Antispam Honeypot Field - ABSOLUTELY HIDDEN */}
                    <div className="sr-only absolute -top-9999 -left-9999">
                      <label htmlFor="hero_website_url">Efterlad dette felt tomt</label>
                      <input
                        id="hero_website_url"
                        type="text"
                        name="website_url"
                        value={formData.website_url}
                        onChange={handleChange}
                        tabIndex="-1"
                        autoComplete="off"
                      />
                    </div>

                    {/* Name */}
                    <div className="space-y-1">
                      <label htmlFor="hero_name" className="block text-[10px] sm:text-xs font-bold text-slate-700 uppercase tracking-wider">
                        Navn <span className="text-red-600">*</span>
                      </label>
                      <input
                        id="hero_name"
                        type="text"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Dit fulde navn"
                        className="w-full px-3 py-2.5 rounded-lg border border-slate-300 bg-white focus:border-[#0e227b] focus:ring-2 focus:ring-[#0e227b]/20 outline-none transition-all text-xs sm:text-sm text-slate-900 placeholder-slate-400"
                      />
                    </div>

                    {/* Contact Grid: Email & Phone */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <label htmlFor="hero_email" className="block text-[10px] sm:text-xs font-bold text-slate-700 uppercase tracking-wider">
                          E-mail <span className="text-red-600">*</span>
                        </label>
                        <input
                          id="hero_email"
                          type="email"
                          name="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="din@email.dk"
                          className="w-full px-3 py-2.5 rounded-lg border border-slate-300 bg-white focus:border-[#0e227b] focus:ring-2 focus:ring-[#0e227b]/20 outline-none transition-all text-xs sm:text-sm text-slate-900 placeholder-slate-400"
                        />
                      </div>

                      <div className="space-y-1">
                        <label htmlFor="hero_phone" className="block text-[10px] sm:text-xs font-bold text-slate-700 uppercase tracking-wider">
                          Telefon <span className="text-red-600">*</span>
                        </label>
                        <input
                          id="hero_phone"
                          type="tel"
                          name="phone"
                          required
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="F.eks. 12345678"
                          className="w-full px-3 py-2.5 rounded-lg border border-slate-300 bg-white focus:border-[#0e227b] focus:ring-2 focus:ring-[#0e227b]/20 outline-none transition-all text-xs sm:text-sm text-slate-900 placeholder-slate-400"
                        />
                      </div>
                    </div>

                    {/* Address */}
                    <div className="space-y-1">
                      <label htmlFor="hero_address" className="block text-[10px] sm:text-xs font-bold text-slate-700 uppercase tracking-wider">
                        Adresse / By <span className="text-red-600">*</span>
                      </label>
                      <input
                        id="hero_address"
                        type="text"
                        name="address"
                        required
                        value={formData.address}
                        onChange={handleChange}
                        placeholder="Vejnavn, husnummer, postnr. & by"
                        className="w-full px-3 py-2.5 rounded-lg border border-slate-300 bg-white focus:border-[#0e227b] focus:ring-2 focus:ring-[#0e227b]/20 outline-none transition-all text-xs sm:text-sm text-slate-900 placeholder-slate-400"
                      />
                    </div>



                    {/* Message */}
                    <div className="space-y-1">
                      <label htmlFor="hero_message" className="block text-[10px] sm:text-xs font-bold text-slate-700 uppercase tracking-wider">
                        Besked / Detaljer <span className="text-red-600">*</span>
                      </label>
                      <textarea
                        id="hero_message"
                        name="message"
                        required
                        rows="4"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="F.eks. antal vinduer, specielle adgangsforhold..."
                        className="w-full px-3 py-2.5 rounded-lg border border-slate-300 bg-white focus:border-[#0e227b] focus:ring-2 focus:ring-[#0e227b]/20 outline-none transition-all text-xs sm:text-sm text-slate-900 placeholder-slate-400 resize-none"
                      ></textarea>
                    </div>

                    {/* Error Message */}
                    {status.error && (
                      <div className="p-3 bg-rose-50 border border-rose-200 rounded-lg text-rose-700 text-xs flex items-start space-x-2">
                        <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                        <span>{status.error}</span>
                      </div>
                    )}

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={status.submitting}
                      className="w-full bg-[#0e227b] hover:bg-[#0e227b]/90 disabled:bg-[#0e227b]/50 text-white font-bold py-3 px-4 rounded-full shadow-md hover:shadow-lg transition-all duration-200 flex items-center justify-center space-x-2 cursor-pointer text-xs sm:text-sm"
                    >
                      {status.submitting ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin text-white" />
                          <span>Sender...</span>
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4 text-white" />
                          <span>Send forespørgsel</span>
                        </>
                      )}
                    </button>

                    <p className="text-center text-[10px] text-slate-500 leading-tight">
                      Ved indsendelse accepterer du, at jeg kontakter dig angående dit tilbud.
                    </p>
                  </form>
                )}

              </div>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
}
